/**
 * Navigation Bar
 *
 * Persistent navigation across all pages.
 * Responsive: hamburger menu on mobile, horizontal nav on desktop.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useViewport } from '../hooks/useViewport';
import { useNotifications } from '../hooks/useNotifications';
import { NotificationBell, MobileNotificationsPanel } from './notifications';
import { TradeModal } from './TradeModal';
import type { TradeNotification } from '@drawn-of-war/shared';
import styles from './NavBar.module.css';

/**
 * Hamburger button component for mobile menu toggle
 */
interface HamburgerButtonProps {
  isOpen: boolean;
  onClick: () => void;
}

function HamburgerButton({ isOpen, onClick }: HamburgerButtonProps) {
  return (
    <button
      className={styles.hamburger}
      onClick={onClick}
      aria-label={isOpen ? 'Close menu' : 'Open menu'}
      aria-expanded={isOpen}
    >
      <span className={`${styles.hamburgerLine} ${isOpen ? styles.hamburgerOpen : ''}`} />
      <span className={`${styles.hamburgerLine} ${isOpen ? styles.hamburgerOpen : ''}`} />
      <span className={`${styles.hamburgerLine} ${isOpen ? styles.hamburgerOpen : ''}`} />
    </button>
  );
}

/**
 * Mobile navigation drawer (slide-out menu)
 */
interface MobileNavDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

function MobileNavDrawer({ isOpen, onClose }: MobileNavDrawerProps) {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Close drawer on route change
  useEffect(() => {
    if (isOpen) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSignOut = async () => {
    await signOut();
    onClose();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`${styles.backdrop} ${isOpen ? styles.backdropOpen : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <nav
        className={`${styles.drawer} ${isOpen ? styles.drawerOpen : ''}`}
        aria-label="Mobile navigation"
      >
        <div className={styles.drawerHeader}>
          <Link to="/home" className={styles.drawerLogo} onClick={onClose}>
            <img
              src="/assets/branding/logo-icon.jpeg"
              alt="Drawn of War Logo"
              className={styles.drawerLogoImage}
            />
            <span className={styles.drawerLogoText}>Drawn of War</span>
          </Link>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close menu"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <div className={styles.drawerContent}>
          <Link
            to="/home"
            className={`${styles.drawerLink} ${isActive('/home') ? styles.drawerLinkActive : ''}`}
            onClick={onClose}
          >
            Home
          </Link>
          <Link
            to="/gallery"
            className={`${styles.drawerLink} ${isActive('/gallery') ? styles.drawerLinkActive : ''}`}
            onClick={onClose}
          >
            Gallery
          </Link>
          <Link
            to="/create"
            className={`${styles.drawerLink} ${isActive('/create') ? styles.drawerLinkActive : ''}`}
            onClick={onClose}
          >
            Create
          </Link>
          <Link
            to="/battles"
            className={`${styles.drawerLink} ${isActive('/battles') ? styles.drawerLinkActive : ''}`}
            onClick={onClose}
          >
            Battle
          </Link>
          <Link
            to="/challenges"
            className={`${styles.drawerLink} ${isActive('/challenges') ? styles.drawerLinkActive : ''}`}
            onClick={onClose}
          >
            Challenge
          </Link>
          <Link
            to="/adventure"
            className={`${styles.drawerLink} ${isActive('/adventure') ? styles.drawerLinkActive : ''}`}
            onClick={onClose}
          >
            Adventure
          </Link>
          <Link
            to="/about"
            className={`${styles.drawerLink} ${isActive('/about') ? styles.drawerLinkActive : ''}`}
            onClick={onClose}
          >
            About
          </Link>
          <a
            href="https://discord.gg/YFTVtMVG4s"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.discordLink}
            onClick={onClose}
          >
            <svg className={styles.discordIcon} viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
            Join Discord
          </a>
        </div>

        <div className={styles.drawerFooter}>
          {user && (
            <>
              <div className={styles.drawerUserInfo}>
                {user.displayName || user.email}
              </div>
              <button onClick={handleSignOut} className={styles.drawerSignOutButton}>
                Sign Out
              </button>
            </>
          )}
        </div>
      </nav>
    </>
  );
}

/**
 * Main NavBar component
 */
export function NavBar() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useViewport();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [tradeModalTarget, setTradeModalTarget] = useState<{ userId: string; displayName: string } | null>(null);

  // Get notification state for mobile panel
  const {
    notifications,
    isLoading: notificationsLoading,
    markAsRead,
    markAllAsRead,
    acceptInvite,
    declineInvite,
    acceptTrade,
    declineTrade
  } = useNotifications();

  // Handle viewing a trade - opens the TradeModal (mobile)
  const handleViewTrade = useCallback((tradeId: string) => {
    const notification = notifications.find(n => n.id === `trade-${tradeId}`);
    if (notification && notification.type === 'trade') {
      const tradeData = notification.data as TradeNotification;
      setTradeModalTarget({
        userId: tradeData.withUser.id,
        displayName: tradeData.withUser.displayName
      });
      setNotificationsOpen(false); // Close the panel
    }
  }, [notifications]);

  async function handleLogout() {
    await signOut();
    navigate('/login');
  }

  // Don't show navbar on auth pages
  if (!user) {
    return null;
  }

  // Helper to determine if a link is active
  const isActive = (path: string) => location.pathname === path;

  // Mobile navigation
  if (isMobile) {
    return (
      <>
        <nav className={styles.navbarMobile}>
          <HamburgerButton
            isOpen={drawerOpen}
            onClick={() => setDrawerOpen(!drawerOpen)}
          />
          <Link to="/home" className={styles.mobileLogo}>
            <img
              src="/assets/branding/logo-icon.jpeg"
              alt="Drawn of War Logo"
              className={styles.mobileLogoImage}
            />
          </Link>
          <NotificationBell
            isMobile
            onMobileOpen={() => setNotificationsOpen(true)}
          />
        </nav>
        <MobileNavDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
        />
        <MobileNotificationsPanel
          isOpen={notificationsOpen}
          onClose={() => setNotificationsOpen(false)}
          notifications={notifications}
          isLoading={notificationsLoading}
          onMarkAsRead={markAsRead}
          onMarkAllAsRead={markAllAsRead}
          onAcceptInvite={acceptInvite}
          onDeclineInvite={declineInvite}
          onAcceptTrade={acceptTrade}
          onDeclineTrade={declineTrade}
          onViewTrade={handleViewTrade}
        />

        {/* Trade Modal - opened when viewing a trade from mobile notifications */}
        {tradeModalTarget && (
          <TradeModal
            isOpen={true}
            onClose={() => setTradeModalTarget(null)}
            targetUserId={tradeModalTarget.userId}
            targetDisplayName={tradeModalTarget.displayName}
          />
        )}
      </>
    );
  }

  // Desktop navigation (unchanged)
  return (
    <nav className={styles.navbar}>
      <div className={styles.leftSection}>
        <Link to="/home" className={styles.logo}>
          <img
            src="/assets/branding/logo-icon.jpeg"
            alt="Drawn of War Logo"
            className={styles.logoImage}
          />
          <span className={styles.logoText}>
            Drawn of War
          </span>
        </Link>

        <div className={styles.navLinks}>
          <Link
            to="/home"
            className={`${styles.navLink} ${isActive('/home') ? styles.active : ''}`}
          >
            Home
          </Link>
          <Link
            to="/gallery"
            className={`${styles.navLink} ${isActive('/gallery') ? styles.active : ''}`}
          >
            Gallery
          </Link>
          <Link
            to="/create"
            className={`${styles.navLink} ${isActive('/create') ? styles.active : ''}`}
          >
            Create
          </Link>
          <Link
            to="/battles"
            className={`${styles.navLink} ${isActive('/battles') ? styles.active : ''}`}
          >
            Battle
          </Link>
          <Link
            to="/challenges"
            className={`${styles.navLink} ${isActive('/challenges') ? styles.active : ''}`}
          >
            Challenge
          </Link>
          <Link
            to="/adventure"
            className={`${styles.navLink} ${isActive('/adventure') ? styles.active : ''}`}
          >
            Adventure
          </Link>
          <Link
            to="/about"
            className={`${styles.navLink} ${isActive('/about') ? styles.active : ''}`}
          >
            About
          </Link>
        </div>
      </div>

      <div className={styles.userSection}>
        <a
          href="https://discord.gg/YFTVtMVG4s"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.discordButton}
        >
          <svg className={styles.discordIconSmall} viewBox="0 0 24 24" fill="currentColor">
            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
          </svg>
          Discord
        </a>
        <NotificationBell />
        <span className={styles.username}>
          {user.displayName || user.email}
        </span>
        <button
          onClick={handleLogout}
          className={styles.logoutButton}
        >
          Logout
        </button>
      </div>
    </nav>
  );
}
